import { useState } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip, alpha, styled, CircularProgress } from '@mui/material';
import type { GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { RefreshOutlined, SendOutlined } from '@mui/icons-material';
import { Table } from '../../../components/Table/Table';
import {
  useGetPendingInvitationsQuery,
  useResendInvitationMutation,
} from '../../../services/restApi/queries/users.service';
import type { InviteVM } from '@tto/shared-models';
import { toast } from 'react-toastify';

type InviteDisplayStatus = 'PENDING' | 'EXPIRED';

const InviteStatusChip = styled(Chip)<{ inviteStatus: InviteDisplayStatus }>(({ theme, inviteStatus }) => {
  const colors =
    inviteStatus === 'EXPIRED'
      ? {
          bg: alpha(theme.palette.warning.main, 0.1),
          color: theme.palette.warning.dark,
        }
      : {
          bg: alpha(theme.palette.info.main, 0.1),
          color: theme.palette.info.dark,
        };

  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
  };
});

const getEffectiveStatus = (invite: InviteVM): InviteDisplayStatus => {
  if (invite.status === 'EXPIRED') return 'EXPIRED';
  if (invite.status === 'PENDING' && new Date(invite.expiresAt).getTime() < Date.now()) return 'EXPIRED';
  return 'PENDING';
};

export const PendingInviteList = () => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });

  const { data: invites, isLoading, isFetching, refetch, error } = useGetPendingInvitationsQuery();
  const [resendInvitation, { isLoading: isResending }] = useResendInvitationMutation();
  const [resendingId, setResendingId] = useState<string | null>(null);

  const handleResend = async (invite: InviteVM) => {
    setResendingId(invite.id);
    try {
      await resendInvitation(invite.id).unwrap();
      toast.success(`Invitation resent to ${invite.email}`);
    } catch (err: any) {
      const errorMessage =
        err?.data?.errorMessage || err?.data || err?.message || err?.errorMessage || 'Failed to resend invitation';
      toast.error(errorMessage);
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: GridColDef<InviteVM>[] = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams<InviteVM>) => (
        <Typography variant='body2' fontWeight={600}>
          {params.row.email}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams<InviteVM>) => {
        const status = getEffectiveStatus(params.row);
        return <InviteStatusChip inviteStatus={status} label={status} size='small' />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Sent',
      width: 150,
      renderCell: (params: GridRenderCellParams<InviteVM>) => (
        <Typography variant='body2'>{formatDate(params.row.createdAt)}</Typography>
      ),
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 150,
      renderCell: (params: GridRenderCellParams<InviteVM>) => {
        const status = getEffectiveStatus(params.row);
        return (
          <Typography variant='body2' color={status === 'EXPIRED' ? 'warning.main' : 'text.primary'}>
            {formatDate(params.row.expiresAt)}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams<InviteVM>) => {
        const isThisResending = resendingId === params.row.id;
        return (
          <Tooltip title='Resend invitation'>
            <span>
              <IconButton
                size='small'
                color='primary'
                onClick={(e) => {
                  e.stopPropagation();
                  handleResend(params.row);
                }}
                disabled={isResending}
              >
                {isThisResending ? <CircularProgress size={18} /> : <SendOutlined fontSize='small' />}
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const rows = invites ?? [];

  return (
    <>
      {/* Toolbar */}
      <Box display='flex' alignItems='center' justifyContent='flex-end' mb={2} gap={1}>
        <Tooltip title='Refresh'>
          <IconButton onClick={() => refetch()} color='primary'>
            <RefreshOutlined />
          </IconButton>
        </Tooltip>
        {rows.length > 0 && (
          <Typography variant='body2' color='text.secondary'>
            {rows.length} {rows.length === 1 ? 'invitation' : 'invitations'}
          </Typography>
        )}
      </Box>

      {/* Table */}
      <Table
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 20, 50]}
        loading={isLoading}
        refetch={refetch}
        isFetching={isFetching}
        checkboxSelection={false}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        paginationMode='client'
        totalRows={rows.length}
        error={!!error}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            background: (theme) => alpha(theme.colors.primary.main, 0.3),
            fontWeight: 600,
            borderRadius: 0,
          },
        }}
      />
    </>
  );
};
